var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useCallback } from 'react';
import { Button, List, Typography, Modal, Form, Input, Select, Checkbox, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { toJS } from 'mobx';
import { Trans, useTranslation } from 'react-i18next';
import ParameterEditor from './ParameterEditor';
import { useScenarioItemValidator } from './hooks/useScenarioItemValidator';
import { SCENARIO_THEME } from './scenarioTheme';
const { Title } = Typography;
const Step_DataLoaders = ({ formData, setFormData, availableLoaders, position, allLoaderAliases, availableProviders }) => {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingLoaderInfo, setEditingLoaderInfo] = useState(null);
    const [modalLoaderData, setModalLoaderData] = useState({});
    const [form] = Form.useForm();
    const [isMainFormValid, setIsMainFormValid] = useState(false);
    const theme = position === 'before' ? SCENARIO_THEME.loadersPreCondition : SCENARIO_THEME.loadersPostCondition;
    const IconComponent = theme.icon;
    const handleAddLoader = () => {
        setEditingLoaderInfo(null);
        setModalLoaderData({ params: {}, confidentialData: false });
        form.resetFields();
        setIsMainFormValid(false);
        setIsModalVisible(true);
    };
    const handleEditLoader = (loader, index) => {
        setEditingLoaderInfo({ index, data: loader });
        setModalLoaderData(loader);
        form.setFieldsValue({ name: loader.name, alias: loader.alias, confidentialData: loader.confidentialData || false });
        setIsMainFormValid(true);
        setIsModalVisible(true);
    };
    const handleCancel = () => {
        setIsModalVisible(false);
    };
    const handleOk = () => {
        form.validateFields().then(values => {
            const finalLoader = {
                name: values.name,
                alias: values.alias,
                params: modalLoaderData.params || {},
                confidentialData: values.confidentialData || false,
            };
            const newList = [...formData];
            if (editingLoaderInfo) {
                newList[editingLoaderInfo.index] = finalLoader;
            }
            else {
                newList.push(finalLoader);
            }
            setFormData(newList);
            handleCancel();
        }).catch(info => {
            console.log('Validate Failed:', info);
        });
    };
    const handleRemoveLoader = (index) => {
        const newList = [...formData];
        newList.splice(index, 1);
        setFormData(newList);
    };
    const handleLoaderTypeChange = useCallback((name) => {
        setModalLoaderData(prev => (Object.assign(Object.assign({}, prev), { name, params: {} })));
    }, []);
    const selectedLoaderDef = toJS(availableLoaders.find(loader => loader.name === modalLoaderData.name));
    const isEditing = !!editingLoaderInfo;
    const isOkDisabled = useScenarioItemValidator({
        isModalVisible,
        isMainFormValid,
        modalData: modalLoaderData,
        selectedDef: selectedLoaderDef,
    });
    return (React.createElement("div", null,
        React.createElement(Space, { style: { marginBottom: 24 } },
            React.createElement(IconComponent, { style: { fontSize: '20px', color: theme.color } }),
            React.createElement(Title, { level: 4, style: { margin: 0, color: theme.color } }, theme.name)),
        React.createElement(Typography.Paragraph, { style: { marginBottom: 24, color: '#666' } },
            React.createElement(Trans, { i18nKey: "loadersStepDescription", defaults: "Here you can load additional data. Loaders are executed sequentially. Data loaded here will be available in subsequent steps." })),
        React.createElement(List, { bordered: true, dataSource: formData, renderItem: (item, index) => {
                const loaderDef = availableLoaders.find(l => l.name === item.name);
                const title = loaderDef ? loaderDef.title : item.name;
                return (React.createElement(List.Item, { actions: [
                        React.createElement(Button, { type: "link", onClick: () => handleEditLoader(item, index) },
                            React.createElement(Trans, { i18nKey: "edit", defaults: "Edit" })),
                        React.createElement(Button, { type: "link", danger: true, onClick: () => handleRemoveLoader(index) },
                            React.createElement(Trans, { i18nKey: "remove", defaults: "Remove" }))
                    ] },
                    React.createElement("div", null,
                        React.createElement(Typography.Text, { strong: true }, item.alias),
                        " - (",
                        title,
                        ")",
                        item.confidentialData && (React.createElement(Typography.Text, { type: "secondary", style: { marginLeft: 8 } },
                            "[",
                            React.createElement(Trans, { i18nKey: "confidential" }),
                            "]")))));
            } }),
        React.createElement(Button, { type: "dashed", onClick: handleAddLoader, style: { marginTop: '16px' } },
            React.createElement(PlusOutlined, null),
            " ",
            React.createElement(Trans, { i18nKey: "addLoader", defaults: "Add Loader" })),
        React.createElement(Modal, { title: isEditing ? t('editDataLoader', 'Edit Data Loader') : t('addDataLoader', 'Add Data Loader'), open: isModalVisible, onOk: handleOk, onCancel: handleCancel, okButtonProps: { disabled: isOkDisabled }, okText: isEditing ? t('save', 'Save') : t('add', 'Add'), destroyOnClose: true, width: 700, bodyStyle: { maxHeight: '70vh', overflowY: 'auto' } },
            React.createElement(Form, { form: form, layout: "vertical", name: "form_in_modal", initialValues: {}, onValuesChange: () => {
                    setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                        try {
                            yield form.validateFields();
                            setIsMainFormValid(true);
                        }
                        catch (_a) {
                            setIsMainFormValid(false);
                        }
                    }), 0);
                } },
                React.createElement(Form.Item, { name: "name", label: React.createElement(Trans, { i18nKey: "loaderType", defaults: "Loader Type" }), rules: [{ required: true, message: React.createElement(Trans, { i18nKey: "pleaseSelectLoaderType", defaults: "Please select a loader type!" }) }] },
                    React.createElement(Select, { placeholder: t('selectLoaderPlaceholder', 'Select a loader'), onChange: handleLoaderTypeChange, disabled: isEditing }, availableLoaders.map(loader => (React.createElement(Select.Option, { key: loader.name, value: loader.name }, loader.title))))),
                React.createElement(Form.Item, { name: "alias", label: React.createElement(Trans, { i18nKey: "loaderAlias", defaults: "Alias" }), rules: [
                        { required: true, message: React.createElement(Trans, { i18nKey: "pleaseInputLoaderAlias", defaults: "Please input an alias for the loader!" }) },
                        {
                            validator: (_, value) => {
                                const isOriginalAlias = isEditing && value === (editingLoaderInfo === null || editingLoaderInfo === void 0 ? void 0 : editingLoaderInfo.data.alias);
                                if (isOriginalAlias || !allLoaderAliases.includes(value)) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error(t('aliasAlreadyInUse', 'This alias is already in use.')));
                            }
                        }
                    ], help: t('loaderAliasHelp', "A unique name to reference this loader's data in later steps.") },
                    React.createElement(Input, { disabled: isEditing })),
                React.createElement(Form.Item, { name: "confidentialData", valuePropName: "checked", style: { marginBottom: 16 } },
                    React.createElement(Checkbox, null,
                        React.createElement(Trans, { i18nKey: "containsConfidentialData" }),
                        React.createElement(Typography.Text, { type: "secondary", style: { display: 'block', fontSize: '12px' } },
                            React.createElement(Trans, { i18nKey: "containsConfidentialDataTooltip" }))))),
            (selectedLoaderDef === null || selectedLoaderDef === void 0 ? void 0 : selectedLoaderDef.paramsSchema) && Object.keys(selectedLoaderDef.paramsSchema).length > 0 && (React.createElement("div", null,
                React.createElement(Typography.Title, { level: 5, style: { marginTop: 16 } },
                    React.createElement(Trans, { i18nKey: "parametersTitle", defaults: "Parameters" })),
                React.createElement(ParameterEditor, { schema: selectedLoaderDef.paramsSchema, formData: modalLoaderData.params, onChange: (params) => setModalLoaderData(prev => (Object.assign(Object.assign({}, prev), { params }))), availableProviders: availableProviders }))))));
};
export default Step_DataLoaders;
